import { useSyncExternalStore } from 'react';
import type { CustomQuestionDefinition } from './CustomQuestionApi';

/**
 * The custom question definitions the page knows about.
 *
 * A form question of type `custom` only carries the id of the definition it
 *  uses; the code itself arrives alongside the form, from
 *  CustomQuestions::forForm. Rather than thread that through every component
 *  that renders a question - the designer, the public form, the submission
 *  panel - it lands here on the way past and is read where it is needed.
 *
 * Only ever holds published code. The editor renders its draft directly
 *  instead of registering it, so nothing half-written can reach a form.
 */
let definitions: Record<number, CustomQuestionDefinition> = {};

const listeners = new Set<() => void>();

function emit() {
    listeners.forEach(listener => listener());
}

/**
 * Merge in what came with a form. Called from the query layer, so it covers
 *  every route that loads a form or a submission.
 */
export function registerCustomQuestions(
    incoming?: Record<string, CustomQuestionDefinition> | null,
) {
    if (!incoming || typeof incoming !== 'object') {
        return;
    }

    const ids = Object.keys(incoming);

    if (!ids.length) {
        return;
    }

    let changed = false;
    const next = { ...definitions };

    for (const id of ids) {
        const definition = incoming[id];

        if (!definition || typeof definition !== 'object') {
            continue;
        }

        const key = parseInt(id);
        const existing = next[key];

        // Versions are immutable, so the version id is enough to know whether
        //  anything actually changed - and avoids re-rendering every frame on
        //  a refetch that brought back the same code
        if (existing && existing.version === definition.version) {
            continue;
        }

        next[key] = definition;
        changed = true;
    }

    if (changed) {
        definitions = next;
        emit();
    }
}

/**
 * Drop one that was deleted. Registering only ever merges - a refetch that
 *  leaves a question out can't tell it was deleted from it not being on that
 *  form - so a delete has to say so.
 */
export function forgetCustomQuestion(id?: number | string | null) {
    const key = parseInt(`${id ?? ''}`);

    if (!key || !(key in definitions)) {
        return;
    }

    const next = { ...definitions };
    delete next[key];
    definitions = next;
    emit();
}

export function getCustomQuestion(
    id?: number | string | null,
): CustomQuestionDefinition | null {
    const key = parseInt(`${id ?? ''}`);

    return key ? (definitions[key] ?? null) : null;
}

function subscribe(listener: () => void) {
    listeners.add(listener);

    return () => {
        listeners.delete(listener);
    };
}

/**
 * The definition a form question points at, or null while it is still loading
 *  (or if it was deleted out from under the form).
 */
export function useCustomQuestion(
    id?: number | string | null,
): CustomQuestionDefinition | null {
    return useSyncExternalStore(
        subscribe,
        () => getCustomQuestion(id),
        () => getCustomQuestion(id),
    );
}

/**
 * Used by the designer's Add Element panel, which lists what a business can
 *  add rather than what one form happens to use.
 */
export function useCustomQuestions(): CustomQuestionDefinition[] {
    const current = useSyncExternalStore(
        subscribe,
        () => definitions,
        () => definitions,
    );

    return Object.values(current);
}

/**
 * Tests and the editor's preview, which stands in a definition that was never
 *  published.
 */
export function resetCustomQuestions(
    next: Record<number, CustomQuestionDefinition> = {},
) {
    definitions = next;
    emit();
}

/**
 * What each custom question on the page last said about its own answer.
 *
 * A frame reports validity whenever it likes, but the form validates all at
 *  once and synchronously, so the latest word from each question is kept here
 *  for Questions.getValidationError to read. Keyed by form question id - the
 *  same question type on two questions validates separately.
 */
type Validity = { isValid: boolean; message: string };

const validity: Record<string, Validity> = {};

export function setCustomQuestionValidity(
    questionId: number | string | null | undefined,
    next: Validity,
) {
    if (!questionId) {
        return;
    }

    validity[`${questionId}`] = next;
}

export function clearCustomQuestionValidity(
    questionId: number | string | null | undefined,
) {
    if (questionId) {
        delete validity[`${questionId}`];
    }
}

export function getCustomQuestionValidity(
    questionId: number | string | null | undefined,
): Validity | null {
    return questionId ? (validity[`${questionId}`] ?? null) : null;
}
